import { useQueries, useQuery } from '@tanstack/react-query';
import React from 'react';
import PolicyApi from '../../api/PolicyApi';
import SearchApi from '../../api/SearchApi';
import { errorToast } from '../Toast';
import {
	GRACE_AND_LAPSE_QUERY_KEY,
	SEARCH_ERROR,
	SEARCH_QUERY_KEY,
} from './constants';
import {
	getCurrentGraceLapse,
	isPolicy2SearchResult,
	policiesPossiblyInGrace,
} from './util';

export const useSearchQuery = (search: string, authToken: string) => {
	const {
		isLoading: searchResponseIsLoading,
		data: searchResponse,
		error,
	} = useQuery({
		refetchOnWindowFocus: false,
		queryKey: [SEARCH_QUERY_KEY, search, authToken],
		queryFn: async () => {
			const { data } = await SearchApi.search(authToken, search);
			return data;
		},
	});

	const graceLapseAndReinstatementInfoQueries = useQueries({
		queries: searchResponse?.results
			? policiesPossiblyInGrace(searchResponse.results).map(
					(result, index) => ({
						queryKey: [
							GRACE_AND_LAPSE_QUERY_KEY,
							index,
							authToken,
							result.locator,
						],
						queryFn: async () => {
							const { data } =
								await PolicyApi.getGraceLapseAndReinstatementInfo(
									authToken,
									result.locator,
								);
							return data;
						},
					}),
			  )
			: [],
	});

	const getResultsWithGraceLapseAndReinstatementInfo = React.useMemo(() => {
		const getResults = () => {
			try {
				const graceDict: any = {};
				graceLapseAndReinstatementInfoQueries.forEach(
					(graceLapseAndReinstatementData) => {
						const data = graceLapseAndReinstatementData.data
							? graceLapseAndReinstatementData.data
							: null;
						const unsettledGrace = getCurrentGraceLapse(data);
						if (unsettledGrace) {
							const gracePeriodStartTimestamp = unsettledGrace.gracePeriod
								? unsettledGrace.gracePeriod.startTimestamp
								: null;
							const policyLocator = unsettledGrace.gracePeriod
								? unsettledGrace.gracePeriod.policyLocator
								: null;
							graceDict[policyLocator] = gracePeriodStartTimestamp;
						}
					},
				);

				const searchData = searchResponse?.results?.map((s) => {
					if (isPolicy2SearchResult(s)) {
						const {
							policy2: { locator },
						} = s;
						if (Object.keys(graceDict).includes(locator)) {
							s.gracePeriodStart = graceDict[locator];
						}
					}
					return s;
				});

				return searchData;
			} catch (error) {
				errorToast(SEARCH_ERROR);
				return [];
			}
		};

		return getResults();
	}, [graceLapseAndReinstatementInfoQueries, searchResponse?.results]);

	const isLoading =
		searchResponseIsLoading ||
		graceLapseAndReinstatementInfoQueries.some(({ isLoading }) => isLoading);

	return {
		isLoading,
		data: search === '' ? null : getResultsWithGraceLapseAndReinstatementInfo,
		error,
	};
};
